import Router from 'vue-router';
import Vue from 'vue';
import store from '@/store'
import request, { configureHttpAuth, readStoredToken } from '@/api/http'
import { pageLoadFeedback } from '@/runtime/page-load-feedback'

Vue.use(Router);

const vueRouter = new Router({
  mode: 'hash',
  routes: [
    {
      path: '/',
      redirect: '/home'  // 添加根路径重定向
    },
    {
      path: '/home',
      name: '主页',
      component: () =>
        import(/* webpackChunkName: "page" */ '@/page/index'),
      meta: { requiresAuth: true }  // 需要登录
    },
    {
      path: '/build/:id',
      name: 'build',
      component: () =>
        import(/* webpackChunkName: "page" */ '@/page/build'),
      meta: { requiresAuth: true }  // 需要登录
    },
    {
      path: '/components/:id/edit',
      name: 'component-resource-edit',
      component: () => import(/* webpackChunkName: "page" */ '@/page/build'),
      meta: { requiresAuth: true }
    },
    {
      path: '/templates/component/:id/edit',
      name: 'template-component-edit',
      component: () => import(/* webpackChunkName: "page" */ '@/page/build'),
      meta: { requiresAuth: true }
    },
    {
      path: '/templates/page/:id/edit',
      name: 'template-page-edit',
      component: () => import(/* webpackChunkName: "page" */ '@/page/build'),
      meta: { requiresAuth: true }
    },
    {
      path: '/templates/page/:id/preview',
      name: 'template-page-preview',
      component: () => import(/* webpackChunkName: "page" */ '@/page/view'),
      meta: { requiresAuth: true }
    },
    {
      path: '/templates/project/:id/edit',
      name: 'template-project-edit',
      component: () => import(/* webpackChunkName: "page" */ '@/page/build'),
      meta: { requiresAuth: true }
    },
    {
      path: '/templates/project/:id/preview',
      name: 'template-project-preview',
      component: () => import(/* webpackChunkName: "page" */ '@/page/view'),
      meta: { requiresAuth: true }
    },
    {
      path: '/view/:id',
      name: 'view',
      component: () =>
        import(/* webpackChunkName: "page" */ '@/page/view'),
      meta: { requiresAuth: true }  // 需要登录
    },
    {
      path: '/login',
      name: 'login',
      component: () =>
        import(/* webpackChunkName: "page" */ '@/page/login'),
    }
  ]
});
vueRouter.addRoutes([{ path: '/platform-access/:target/:id', name: 'platform-editor-access', component: () => import('@/page/platform-access.vue') }, { path: '/platform-preview/:id', name: 'platform-project-preview', meta: { requiresAuth: true }, component: () => import('@/page/platform-preview.vue') }, { path: '/share/:code', name: 'screen-share', component: () => import('@/page/share.vue') }, { path: '/embedded/:id', name: 'screen-embedded', component: () => import('@/page/embedded.vue') }])
const defaultOwnParams = [
  {
      terms: [
          {
              terms: [
                  {
                      column: 'owner',
                      termType: 'eq',
                      value: 'iot',
                  },
                  {
                      column: 'owner',
                      termType: 'isnull',
                      value: '1',
                      type: 'or',
                  },
              ],
          },
          {
              terms: [
                  {
                      value: "%show\":false%",
                      termType: "nlike",
                      column: "options"
                  }
              ],
              type:'and'
          }
      ],
  },
]

configureHttpAuth({
  getToken: () => readStoredToken() || store.state.user.token || '',
  onUnauthorized: () => {
    localStorage.removeItem('x-access-token')
    store.dispatch('auth/clearPermissions')
    store.dispatch('user/logout')
    if (vueRouter.currentRoute.path !== '/login') vueRouter.replace('/login').catch(() => {})
  }
})

// 全局路由守卫
vueRouter.beforeEach(async (to, from, next) => {
  const ticket = pageLoadFeedback.begin(to.fullPath)
  try {
    // 1. 统一获取 token
    let token = to.query.token || readStoredToken()

    // 如果 token 来自 query，可选择持久化
    if (to.query.token) {
      localStorage.setItem('x-access-token', to.query.token)
    }

    // 2. 不需要鉴权的路由直接放行
    if (!to.matched.some(r => r.meta.requiresAuth)) {
      return next()
    }

    // 3. 未登录 → 跳转登录
    if (!token) {
      return next({
        path: '/login',
        query:
          from.path === '/' || from.path === '/login'
            ? {}
            : { redirect: to.fullPath }
      })
    }

    // 4. 已登录，补齐用户信息
    const userInfo = store.getters['user/userInfo']
    if (!userInfo || Object.keys(userInfo).length === 0) {
      pageLoadFeedback.stage(ticket, '正在校验登录信息…')
      const res = await request.get('/jetlinks/user/detail')
      if (!pageLoadFeedback.current(ticket)) return next()
      if (!res.data || res.data.success === false || (res.data.status && res.data.status !== 200) || !res.data.result || typeof res.data.result !== 'object' || Array.isArray(res.data.result) || !Object.keys(res.data.result).length) throw new Error('登录信息读取失败，请重试')
      await store.dispatch('user/saveLoginInfo', res.data.result)
    }

    // 5. 补齐权限信息
    const permissions = store.getters['auth/permissions']
    if (!permissions || Object.keys(permissions).length === 0) {
      pageLoadFeedback.stage(ticket, '正在读取访问权限…')
      const res = await request.post('/jetlinks/menu/user-own/tree', {
        paging: false,
        terms: defaultOwnParams
      })
      if (!pageLoadFeedback.current(ticket)) return next()
      if (!res.data || res.data.success === false || (res.data.status && res.data.status !== 200) || !Array.isArray(res.data.result)) throw new Error('访问权限读取失败，请重试')
      await store.dispatch('auth/savePermissions', res.data.result)
    }

    // 图片资源直接使用平台注解注册的资源权限，不依赖额外菜单按钮。
    if (!store.getters['auth/resourcePermissionsLoaded']) {
      pageLoadFeedback.stage(ticket, '正在读取资源权限…')
      const res = await request.get('/jetlinks/authorize/me')
      if (!pageLoadFeedback.current(ticket)) return next()
      if (!res.data || res.data.success === false || (res.data.status && res.data.status !== 200)) throw new Error('资源权限读取失败，请重试')
      await store.dispatch('auth/saveResourcePermissions', res.data.result)
    }

    // 6. 一切 OK
    if (pageLoadFeedback.current(ticket)) pageLoadFeedback.stage(ticket, '正在加载页面资源…')
    next()
  } catch (error) {
    // 旧导航迟到的结果不能覆盖新导航，也不能把网络错误当作登录失效。
    if (!pageLoadFeedback.current(ticket)) return next()
    if (error.status === 401) return next('/login')
    pageLoadFeedback.fail(ticket, error.message)
    next(false)
  }
})

vueRouter.afterEach(to => pageLoadFeedback.finish(to.fullPath))
vueRouter.onError(() => pageLoadFeedback.failResource())


export default vueRouter;
